import { useFormik } from 'formik'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import { Status } from '../../components/cards/statusTag'
import Dropdown from '../../components/inputs/dropdown'
import { ImageUploadPreviewModal } from '../../components/modal/app_modals/ImagePreviewModal'
import { TabNav } from '../../components/nav/tabNav'
import { ManageBundleItems } from '../../components/product/manageBundleItems'
import { ManageProductAsGift } from '../../components/product/manageProductAsGift'
import { ManageProductCategories } from '../../components/product/manageProductCategories'
import { ManageProductCombos } from '../../components/product/manageProductCombos'
import { ManageProductDetails } from '../../components/product/manageProductDetails'
import { ManageProductExpiration } from '../../components/product/manageProductExpiry'
import { ManageProductImages } from '../../components/product/manageProductImages'
import { ManageProductInventory } from '../../components/product/manageProductInventory'
import { ManageProductLocations } from '../../components/product/manageProductLocations'
import { ManageProductPriceSlash } from '../../components/product/manageProductPriceSlash'
import { ManageProductSubCategories } from '../../components/product/manageProductSubCategories'
import { ManageProductSuggestions } from '../../components/product/manageProductSuggestions'
import { ManageProductTags } from '../../components/product/manageProductTags'
import { ManageProductVariants } from '../../components/product/manageProductVariants'
import { ManageProductWeight } from '../../components/product/manageProductWeight'
import { mapDeliveryLocationsToForm } from '../../components/product/product-location-mappers'
import {
  buildProductInventoryQuantities,
  getEditableInventoryCentres,
  buildVariantInventoryQuantities,
  getVariantCombinationInventoryKey,
  ProductInventoryQuantities,
  sumInventoryQuantities,
  sumVariantInventoryQuantities,
  VariantInventoryQuantities,
} from '../../components/product/productInventory'
import Spinner from '../../components/spinner/Spinner'
import { PageHeader } from '../../components/typography/PageHeader'
import {
  calculateProductPriceRaw,
  formatCurrency,
  formatDate,
  getProductType,
  getVendorName,
  parseCurrencyToNumber,
  sumArray,
} from '../../helpers'
import { useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  useCreateProductMutation,
  useEditProductMutation,
  useGetProductInventoryQuery,
  useGetSingleProductQuery,
} from '../../redux-toolkits/products/products.slice'
import { useGetSingleVendorQuery } from '../../redux-toolkits/vendors/vendors.slice'
import {
  CreateProductPayload,
  DeliveryTimeType,
  WeightUnits,
} from '../../redux-toolkits/products/products.type'
import {
  CreateProductValues,
  ProductType,
} from '../../redux-toolkits/products/products.type'
import { CreateProductSchema } from '../../validation/validate'

type ProductTabs = 'variant' | 'suggestions'

export const CreateProductPage = () => {
  const { id, type } = useParams<{ id: string; type: ProductType }>()

  const [typeTitle, setTypeTitle] = useState<string>('')
  const [productType, setProductType] = useState<ProductType>('single')
  const [variantComboQuantity, setVariantComboQuantity] = useState(0)
  const [productTab, setProductTab] = useState<ProductTabs>('variant')
  const [uploadedImage, setUploadedImage] = useState<File>()
  const [selectedVendorId, setSelectedVendorId] = useState('')
  const [productInventoryQuantities, setProductInventoryQuantities] =
    useState<ProductInventoryQuantities>({})
  const [variantInventoryQuantities, setVariantInventoryQuantities] =
    useState<VariantInventoryQuantities>({})
  const initializedInventoryKey = useRef('')

  const navigate = useNavigate()
  const { user } = useAppSelector((state) => state.auth)

  const {
    isFetching: fetchingSingleProduct,
    data: productData,
    refetch: refetchProductDetails,
  } = useGetSingleProductQuery(
    {
      id: id ?? '',
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    },
  )

  const { data: selectedVendorData, isFetching: fetchingSelectedVendor } =
    useGetSingleVendorQuery(
      { id: selectedVendorId },
      { skip: !selectedVendorId },
    )

  const selectedVendorFulfilmentCentres = getEditableInventoryCentres(
    selectedVendorData?.data.profile.fulfilmentCentres ?? [],
    user?.fulfillmentCenter,
  )

  const {
    currentData: productInventoryData,
    isFetching: fetchingProductInventory,
  } = useGetProductInventoryQuery(
    { id: id ?? '' },
    { skip: !id, refetchOnMountOrArgChange: true },
  )

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  const [createProduct, { isLoading: creatingProduct }] =
    useCreateProductMutation()

  const [editProduct, { isLoading: editingProduct }] = useEditProductMutation()

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    const selectedFile = event.target.files[0]
    setUploadedImage(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const formik = useFormik({
    initialValues: {
      name: productData?.data?.name ?? '',
      category: productData?.data?.category ?? [],
      subCategory: productData?.data?.subcategory ?? [],
      description: productData?.data?.description ?? '',
      images: productData?.data?.images ?? [],
      price: productData?.data?.price ?? '',
      quantityInStock: productData?.data?.inStockQuantity ?? '',
      vendor: {
        _id: productData?.data?.vendor?._id ?? '',
        name: getVendorName(productData?.data?.vendor) ?? '',
      },
      tags: productData?.data?.tags ?? [],
      isCombo: productData?.data?.isCombo ?? false,
      comboItems:
        productData?.data?.comboItems?.map(({ _id, ...others }) => {
          return {
            ...others,
            items:
              others?.items?.map(({ _id, ...itemOthers }) => ({
                ...itemOthers,
              })) ?? [],
          }
        }) ?? [],
      isActive: productData?.data?.isActive ?? true,
      isVariant: productData?.data?.isVariant ?? false,
      variants:
        productData?.data?.variants?.map(({ _id, ...others }) => {
          return { ...others }
        }) ?? [],
      variantCombinations: productData?.data?.variantCombinations ?? [],
      lowStockAlert: productData?.data?.lowStockAlert ?? false,
      lowStockQuantity: productData?.data?.lowStockQuantity ?? '',
      weight: productData?.data?.weight?.value ?? undefined,
      weightUnit: productData?.data?.weight?.unit ?? 'grams',
      availabilityPolicy:
        productData?.data?.availabilityPolicy ??
        (id ? 'CUSTOM' : 'INHERIT_VENDOR'),
      deliveryLocation: mapDeliveryLocationsToForm(
        productData?.data?.deliveryLocation,
      ),
      creatorDeliveryLocation: mapDeliveryLocationsToForm(
        productData?.data?.vendor?.deliveryLocation,
      ),
      deliveryAreaRestriction: productData?.data?.deliveryAreaRestriction ?? {
        mode: 'ALL_REGION',
        areaIds: [],
      },
      effectiveDeliveryAreaRestriction:
        productData?.data?.effectiveDeliveryAreaRestriction ??
        productData?.data?.vendor?.deliveryAreaRestriction,
      instantDeliveryEnabled:
        productData?.data?.instantDeliveryEnabled ?? false,
      isMarkupEnabled: productData?.data?.isMarkupEnabled ?? false,
      markupValue: productData?.data?.markupValue ?? '',
      addOns: productData?.data?.addOns ?? undefined,
      recommendedProducts: productData?.data?.recommendedProducts ?? undefined,
      bundledProducts: productData?.data?.bundledProducts ?? undefined,
      searchKeywords: productData?.data?.searchKeywords ?? [],
      keywordTyped: '',
      trackExpiry: productData?.data?.trackExpiry ?? false,
      bestBeforeDate: productData?.data?.expirySettings?.bestBeforeDate,
      expiryReminderInterval:
        productData?.data?.expirySettings?.expiryReminderInterval,
      hasDiscount: productData?.data?.hasDiscount ?? false,
      discountDetails: {
        discountType: productData?.data?.discountDetails?.discountType,
        discountValue: productData?.data?.discountDetails?.discountValue,
      },
    } as CreateProductValues,
    enableReinitialize: true,
    validationSchema: CreateProductSchema(productType),
    onSubmit: (values, { resetForm }) => {
      const payload: CreateProductPayload = {
        name: values.name,
        categoryId: values.category.map((cat) => cat._id),
        subcategoryId: values.subCategory.map((subCat) => subCat._id),
        description: values.description,
        images: values.images,
        isActive: values.isActive,
        trackQuantity: true,
        price: parseCurrencyToNumber(values.price),
        inStockQuantity: values.isVariant
          ? undefined
          : Number(values.quantityInStock) || undefined,
        vendorId: values.vendor?._id,
        tags: values.tags.map((tag) => tag._id),
        isCombo: values.isCombo,
        comboItems: values.comboItems,
        isVariant: values.isVariant,
        availabilityPolicy: values.availabilityPolicy,
        deliveryLocation:
          values.availabilityPolicy === 'CUSTOM'
            ? values.deliveryLocation.map((item) => {
                return {
                  locationId: item.locationId,
                  minimumDeliveryTime: item.minimumDeliveryTime,
                  maximumDeliveryTime: item.maximumDeliveryTime,
                  type: item.type as DeliveryTimeType,
                  deliveryTime: item.deliveryTime,
                  locationType: item.locationType,
                }
              })
            : undefined,
        deliveryAreaRestriction:
          values.availabilityPolicy === 'CUSTOM'
            ? {
                mode: values.deliveryAreaRestriction.mode,
                areaIds:
                  values.deliveryAreaRestriction.mode === 'ALL_REGION'
                    ? []
                    : values.deliveryAreaRestriction.areaIds.map((area) =>
                        typeof area === 'string' ? area : area._id,
                      ),
              }
            : undefined,
        variants: values.variants.map((variant) => {
          return {
            variant: variant.variant._id,
            value: variant?.value,
            price: variant.price,
            images: variant.images ?? [],
            markupValue: variant.markupValue,
          }
        }),
        variantCombinations: values.variantCombinations.map((vc) => {
          const combinationKey = getVariantCombinationInventoryKey(vc)
          return {
            ...vc,
            variantCombination: vc.variantCombination.map((item) => {
              return {
                ...item,
                variant: item.variant._id,
              }
            }),
            fulfilmentCentreInventory:
              selectedVendorData?.data.profile.inventoryMode ===
              'FULFILMENT_CENTRE'
                ? selectedVendorFulfilmentCentres.map(({ _id }) => ({
                    fulfilmentCentreId: _id,
                    quantity:
                      variantInventoryQuantities[combinationKey]?.[_id] ?? 0,
                  }))
                : undefined,
          }
        }),
        lowStockAlert: values.lowStockAlert,
        instantDeliveryEnabled: values.instantDeliveryEnabled,
        isMarkupEnabled: values.isMarkupEnabled,
        addOns: values.addOns?.map((i) => i._id),
        recommendedProducts: values.recommendedProducts?.map((i) => i._id),
        bundledProducts: values.bundledProducts?.map((i) => ({
          product: i.product._id,
          bundleQuantity: i.bundleQuantity,
          pin: i.pin ?? null,
        })),
        searchKeywords: values.searchKeywords,
        trackExpiry: values.trackExpiry,
        hasDiscount: values.hasDiscount,
      }

      if (
        selectedVendorData?.data.profile.inventoryMode ===
          'FULFILMENT_CENTRE' &&
        !values.isVariant
      ) {
        payload.inStockQuantity = undefined
        payload.fulfilmentCentreInventory = selectedVendorFulfilmentCentres.map(
          ({ _id }) => ({
            fulfilmentCentreId: _id,
            quantity: productInventoryQuantities[_id] ?? 0,
          }),
        )
      }

      if (payload.lowStockAlert) {
        payload.lowStockQuantity = Number(values.lowStockQuantity)
      }

      if (payload.isMarkupEnabled) {
        payload.markupValue = Number(values.markupValue)
      }

      if (payload.trackExpiry) {
        payload.bestBeforeDate = formatDate(values.bestBeforeDate, 'YYYY-MM-DD')
        payload.expiryReminderInterval = values.expiryReminderInterval
      }

      if (values.weight && values.weightUnit) {
        payload.weight = {
          value: Number(values.weight),
          unit: values.weightUnit as WeightUnits,
        }
      }

      if (payload.hasDiscount && values.discountDetails) {
        payload.discountDetails = {
          discountType: values.discountDetails.discountType,
          discountValue: parseCurrencyToNumber(
            values.discountDetails.discountValue,
          ),
        }
      }

      id
        ? editProduct({ id, body: payload })
        : createProduct(payload).then((resp) => {
            if (resp.data?.success) {
              resetForm()
              navigate(`/products/view/${resp.data.data._id}`)
            }
          })
    },
  })

  const { values, handleSubmit, errors, setFieldValue, isValid, validateForm } =
    formik

  useEffect(() => {
    if (productData?.data) {
      const product = productData.data

      const productType_ = getProductType(product)

      setProductType(productType_)

      if (productType_ === 'bundle') {
        setTypeTitle('Bundle')
      } else {
        setTypeTitle('Product')
      }

      setProductTab(
        productType_ === 'variant' || productType_ === 'variantCombo'
          ? 'variant'
          : 'suggestions',
      )
      setSelectedVendorId(product.vendor?._id ?? '')
    } else if (type) {
      if (type === 'bundle') {
        setTypeTitle('Bundle')
      } else {
        setTypeTitle('Product')
      }

      setProductType(type)

      setProductTab(
        type === 'variant' || type === 'variantCombo'
          ? 'variant'
          : 'suggestions',
      )
    }
  }, [productData, type])

  useEffect(() => {
    const vendor = selectedVendorData?.data.profile
    if (!vendor || vendor.inventoryMode !== 'FULFILMENT_CENTRE') return
    if (
      id &&
      (fetchingSingleProduct ||
        fetchingProductInventory ||
        !productInventoryData?.data)
    )
      return

    const inventoryKey = `${vendor._id}:${id ?? 'new'}:${(
      user?.fulfillmentCenter ?? []
    )
      .map(({ _id }) => _id)
      .join(',')}`
    if (initializedInventoryKey.current === inventoryKey) return

    const fulfilmentCentres = getEditableInventoryCentres(
      vendor.fulfilmentCentres ?? [],
      user?.fulfillmentCenter,
    )
    setProductInventoryQuantities(
      buildProductInventoryQuantities(
        fulfilmentCentres,
        productInventoryData?.data,
      ),
    )
    setVariantInventoryQuantities(
      buildVariantInventoryQuantities(
        values.variantCombinations,
        fulfilmentCentres,
        productInventoryData?.data,
      ),
    )
    initializedInventoryKey.current = inventoryKey
  }, [
    id,
    fetchingSingleProduct,
    fetchingProductInventory,
    productInventoryData,
    selectedVendorData,
    values.variantCombinations,
    user?.fulfillmentCenter,
  ])

  useEffect(() => {
    if (values.isVariant) {
      setVariantComboQuantity(
        values.variantCombinations.reduce(
          (sum, item) => sum + Number(item.inventoryCount),
          0,
        ),
      )
    }

    if (productType === 'bundle') {
      const bundlePrice = sumArray(
        (values.bundledProducts ?? []).map((p) => {
          return (
            calculateProductPriceRaw(
              Number(p.product.price),
              // values.comboItems,
              [],
              p.product.isMarkupEnabled ?? false,
              Number(p.product.markupValue) ?? 0,
            ) * p.bundleQuantity
          )
        }),
      )

      setFieldValue('price', bundlePrice)
    }
  }, [values, productType])

  const usesFulfilmentCentreInventory =
    selectedVendorData?.data.profile.inventoryMode === 'FULFILMENT_CENTRE'
  const waitingForProductInventory =
    Boolean(id) &&
    usesFulfilmentCentreInventory &&
    (fetchingProductInventory || !productInventoryData?.data)
  const allocatedInventoryTotal = values.isVariant
    ? sumVariantInventoryQuantities(
        variantInventoryQuantities,
        values.variantCombinations,
      )
    : sumInventoryQuantities(productInventoryQuantities)
  const stockQuantity = usesFulfilmentCentreInventory
    ? allocatedInventoryTotal
    : values.isVariant
      ? variantComboQuantity
      : values.quantityInStock
  const waitingForStock =
    !selectedVendorData?.data.profile ||
    fetchingSelectedVendor ||
    waitingForProductInventory

  return (
    <Layout>
      {id && fetchingSingleProduct ? (
        <Spinner fullScreen={false} />
      ) : (
        <>
          <form className="flex flex-col gap-10" onSubmit={handleSubmit}>
            <BackButton />

            <div className="flex flex-col md:flex-row flex-wrap w-full items-start gap-4 justify-between">
              <div className="flex flex-col gap-2">
                <PageHeader
                  title={id ? productData?.data?.name : `Create ${typeTitle}`}
                  className="flex items-center gap-4 !my-0 md:w-fit w-full"
                />

                {id && (
                  <div className="flex flex-col gap-4">
                    <p className="text-sec-black text-base font-medium">
                      {formatCurrency(
                        calculateProductPriceRaw(
                          Number(values.price),
                          // values.comboItems,
                          [],
                          values.isMarkupEnabled ?? false,
                          Number(values.markupValue) ?? 0,
                        ),
                      )}
                    </p>

                    <div className="flex items-center gap-2 text-sm">
                      <p>
                        <span className="font-medium">
                          {waitingForStock ? '—' : stockQuantity}
                        </span>{' '}
                        items in stock
                      </p>
                      <p className="text-base">•</p>
                      <p>
                        <span className="font-medium">
                          {(
                            productData?.data?.totalQuantitySold ?? 0
                          ).toLocaleString()}
                        </span>{' '}
                        items sold
                      </p>
                      <p className="text-base">•</p>
                      <p>
                        <span className="font-medium">
                          {(
                            productData?.data?.totalRefunded ?? 0
                          ).toLocaleString()}
                        </span>{' '}
                        items refunded
                      </p>
                      <p className="text-base">•</p>
                      <Status
                        type={productData?.data?.isActive ? 'success' : 'fail'}
                        text={
                          productData?.data?.isActive ? 'Active' : 'Inactive'
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-row gap-4 max-md:order-first max-md:ml-auto">
                <Button
                  type="submit"
                  primary
                  label={!id ? `Create ${typeTitle}` : 'Save Changes'}
                  className="!w-fit"
                  loading={
                    creatingProduct ||
                    editingProduct ||
                    fetchingSelectedVendor ||
                    waitingForProductInventory
                  }
                  disabled={
                    creatingProduct ||
                    editingProduct ||
                    fetchingSelectedVendor ||
                    waitingForProductInventory ||
                    (usesFulfilmentCentreInventory &&
                      selectedVendorFulfilmentCentres.length === 0)
                  }
                  onClick={async () => {
                    await validateForm()
                    if (!isValid) {
                      toast.error('Some errors exist in your form')
                    }
                  }}
                />

                {id && (
                  <Dropdown
                    menuButton={
                      <Button
                        icon="ellipses"
                        className="size-[58px] !px-0"
                        type="button"
                      />
                    }
                    menuItems={[
                      {
                        name: `${
                          productData?.data?.isActive
                            ? 'Deactivate'
                            : 'Activate'
                        }`,
                        value: '',
                        onClick: () => {
                          setFieldValue(
                            'isActive',
                            !productData?.data?.isActive,
                          )
                          handleSubmit()
                        },
                      },
                    ]}
                    menuItemClassName="border-b-0"
                    menuClassName="!w-[200px]"
                  />
                )}
              </div>
            </div>

            {/* IMAGES  */}
            <ManageProductImages
              values={values}
              setFieldValue={setFieldValue}
              handleFileChange={handleFileChange}
              typeTitle={typeTitle}
            />

            <ManageProductDetails
              formik={formik}
              className="pb-10 border-b border-divider-gray"
              typeTitle={typeTitle}
              id={id}
              product={productData?.data}
              productType={productType}
              variantComboQuantity={variantComboQuantity}
              inventoryQuantity={
                usesFulfilmentCentreInventory
                  ? allocatedInventoryTotal
                  : undefined
              }
              usesFulfilmentCentreInventory={usesFulfilmentCentreInventory}
              fulfilmentCentres={selectedVendorFulfilmentCentres}
              onCreatorSelected={(vendorId) => {
                setSelectedVendorId(vendorId)
                initializedInventoryKey.current = ''
                setProductInventoryQuantities({})
                setVariantInventoryQuantities({})
              }}
              onInventoryAdjusted={({ fulfilmentCentreId, quantityDelta }) => {
                if (!fulfilmentCentreId) return
                setProductInventoryQuantities((current) => ({
                  ...current,
                  [fulfilmentCentreId]: Math.max(
                    0,
                    (current[fulfilmentCentreId] ?? 0) + quantityDelta,
                  ),
                }))
              }}
              refetchProductDetails={() => {
                refetchProductDetails()
              }}
            />

            {usesFulfilmentCentreInventory && !waitingForProductInventory ? (
              <ManageProductInventory
                fulfilmentCentres={selectedVendorFulfilmentCentres}
                isVariant={values.isVariant}
                productQuantities={productInventoryQuantities}
                variantCombinations={values.variantCombinations}
                variantQuantities={variantInventoryQuantities}
                onProductQuantityChange={(centreId, quantity) => {
                  setProductInventoryQuantities((current) => ({
                    ...current,
                    [centreId]: quantity,
                  }))
                }}
                onVariantQuantityChange={(
                  combinationKey,
                  centreId,
                  quantity,
                ) => {
                  setVariantInventoryQuantities((current) => ({
                    ...current,
                    [combinationKey]: {
                      ...current[combinationKey],
                      [centreId]: quantity,
                    },
                  }))
                }}
              />
            ) : undefined}

            {/* PRICE SLASH  */}
            <ManageProductPriceSlash
              formik={formik}
              className="pb-10 border-b border-divider-gray"
              typeTitle={typeTitle}
            />

            {/* GIFTING  */}
            {id ? (
              <ManageProductAsGift
                formik={formik}
                className="pb-10 border-b border-divider-gray"
                typeTitle={typeTitle}
                refetch={() => {
                  refetchProductDetails()
                }}
                product={productData?.data}
              />
            ) : undefined}

            {/* EXPIRATION  */}
            <ManageProductExpiration
              formik={formik}
              className="pb-10 border-b border-divider-gray"
              typeTitle={typeTitle}
            />

            {/* WEIGHT  */}
            <ManageProductWeight
              formik={formik}
              className="pb-10 border-b border-divider-gray"
              typeTitle={typeTitle}
            />

            {/* BUNNDLE ITEMS  */}
            {productType === 'bundle' ? (
              <ManageBundleItems
                values={values}
                setFieldValue={setFieldValue}
                errors={(errors?.bundledProducts ?? '') as string}
              />
            ) : undefined}

            {/* TAGS  */}
            <ManageProductTags
              values={values}
              setFieldValue={setFieldValue}
              typeTitle={typeTitle}
            />

            {/* CATEGORIES  */}
            <ManageProductCategories
              values={values}
              setFieldValue={setFieldValue}
              errors={(errors?.category ?? '') as string}
              typeTitle={typeTitle}
            />

            {/* SUB-CATEGORIES  */}
            <ManageProductSubCategories
              values={values}
              setFieldValue={setFieldValue}
              typeTitle={typeTitle}
              errors={(errors.subCategory ?? '') as string}
            />

            {/* COMBOS  */}
            {productType === 'combo' || productType === 'variantCombo' ? (
              <ManageProductCombos
                values={values}
                setFieldValue={setFieldValue}
              />
            ) : undefined}

            {/* LOCATIONS  */}
            <ManageProductLocations
              values={values}
              setFieldValue={setFieldValue}
              errors={(errors?.deliveryLocation ?? '') as string}
              typeTitle={typeTitle}
            />

            <TabNav
              activeTab={productTab}
              setActiveTab={(selected) => {
                setProductTab(selected as ProductTabs)
              }}
              tabs={[
                {
                  name: `${typeTitle} Variants`,
                  id: 'variant',
                },
                {
                  name: `${typeTitle} Suggestions`,
                  id: 'suggestions',
                },
              ].filter((t) => {
                if (
                  productType !== 'variant' &&
                  productType !== 'variantCombo'
                ) {
                  return t.id !== 'variant'
                }
                return true
              })}
              type="nav"
            />

            {/* VARIANTS  */}
            {productTab === 'variant' &&
            (productType === 'variant' || productType === 'variantCombo') ? (
              <ManageProductVariants
                values={values}
                setFieldValue={setFieldValue}
                productDetails={productData?.data}
                usesFulfilmentCentreInventory={usesFulfilmentCentreInventory}
                fulfilmentCentres={selectedVendorFulfilmentCentres}
                onInventoryAdjusted={({
                  fulfilmentCentreId,
                  variantCombinationId,
                  quantityDelta,
                }) => {
                  if (!fulfilmentCentreId || !variantCombinationId) return
                  setVariantInventoryQuantities((current) => ({
                    ...current,
                    [variantCombinationId]: {
                      ...current[variantCombinationId],
                      [fulfilmentCentreId]: Math.max(
                        0,
                        (current[variantCombinationId]?.[fulfilmentCentreId] ??
                          0) + quantityDelta,
                      ),
                    },
                  }))
                }}
              />
            ) : undefined}

            {/* PRODUCT SUGGESTIONS  */}
            {productTab === 'suggestions' ? (
              <ManageProductSuggestions
                values={values}
                setFieldValue={setFieldValue}
                productDetails={productData?.data}
              />
            ) : undefined}
          </form>

          {uploadedImage && (
            <ImageUploadPreviewModal
              image={uploadedImage}
              open={modal === 'imageUploadPreviewModal' && modalOpen}
              setOpen={setModalOpen}
              onCloseCallback={() => {
                setUploadedImage(undefined)
              }}
              onSuccessCallback={(url) => {
                const imagesCopy: string[] = JSON.parse(
                  JSON.stringify(values.images ?? []),
                )
                imagesCopy.push(url)
                setFieldValue('images', imagesCopy)
                setUploadedImage(undefined)
              }}
            />
          )}
        </>
      )}
    </Layout>
  )
}
